import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Empty, Table, TableProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

// services
import NotificationService from 'core/services/notification';
import { onDeleteCountry, onGetCountries } from 'modules/countries/services/countries';

// models
import { Country } from 'modules/countries/models/countries';

// components
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import Icon from 'utils/icon';
import AuthActionComponent from 'components/authAction/authAction';
import NoData from 'components/noData/noData';
import { useTranslation } from 'react-i18next';
import { isRTL } from 'core/services/language';

function ListCountriesPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [countries, setCountries] = useState<Country[]>([]);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [tempDeletedCountry, setTempDeletedCountry] = useState<number>();

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	const columns: TableProps<Country>['columns'] = [
		{
			title: t('COUNTRIES.NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name) => <p className="mb-0 fw-medium">{isRTL() ? name?.ar : name?.en}</p>,
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'active',
			key: 'active',
			render: (active) => (
				<span className={`status fw-medium ${active ? 'active' : 'inactive'}`}>{active ? t('COMMON.INACTIVE') : t('COMMON.ACTIVE')}</span>
			),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, country) => (
				<div className="d-flex gap-2">
					<AuthActionComponent action="country_update">
						<Link to={`${country.id}`}>
							<Icon name="edit" width={35} height={35} />
						</Link>
					</AuthActionComponent>
					<AuthActionComponent action="country_delete">
						<Button type="link" className="p-0" onClick={() => onShowConfirmation(country.id)}>
							<Icon name="delete" width={35} height={35} />
						</Button>
					</AuthActionComponent>
				</div>
			),
		},
	];

	useEffect(() => {
		getCountries();
	}, []);

	const getCountries = async () => {
		try {
			setIsLoading(true);

			const response = await onGetCountries();
			setCountries(response.data.data.countries);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			setIsLoading(false);
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });
			const response = await onGetCountries(page);
			setCountries(response.data.data.countries);
			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			setIsLoadMore(false);
		}
	};

	const onShowConfirmation = (countryId: number) => {
		setShowConfirmation(true);
		setTempDeletedCountry(countryId);
	};

	const onDiscardConfirmation = () => {
		setShowConfirmation(false);
	};

	const onDelete = async () => {
		try {
			setIsDeleting(true);
			await onDeleteCountry(tempDeletedCountry as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('COUNTRIES.DELETED_SUCCESS'));
			setShowConfirmation(false);
			setIsDeleting(false);
			getCountries();
		} catch (error) {
			setIsDeleting(false);
			setShowConfirmation(false);
			NotificationService.error(t('COMMON.OPS'), t('COUNTRIES.DELETE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<h3>{t('COUNTRIES.MANAGEMENT')}</h3>
				{/* <p>The most important info highlights</p> */}
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className={`sectionContainer listData`}>
					<header>
						<div>
							<h5>
								{totalRecords} {t('COUNTRIES.COUNTRIES')}
							</h5>
							<p>{t('COUNTRIES.MANAGE_DESCRIPTION')}</p>
						</div>
						<AuthActionComponent action="country_create">
							<Link to="create">
								<Button type="primary" icon={<PlusOutlined />} style={{ background: '#F1F1F2', color: '#7E8299', border: 'none' }}>
									{t('COMMON.ADD')}
								</Button>
							</Link>
						</AuthActionComponent>
					</header>

					<div style={{ position: 'relative' }}>
						<Table
							columns={columns}
							dataSource={countries}
							scroll={{ x: 600 }}
							rowKey="id"
							pagination={{
								pageSize: 15,
								total: totalRecords,
								showSizeChanger: false,
								current: currentPage,
								onChange: fetchMoreData,
							}}
							locale={{ emptyText: <NoData /> }}
						/>

						{isLoadMore && (
							<div className="loadMoreContainer">
								<LoadingComponent />
							</div>
						)}
					</div>
				</div>
			)}

			<ConfirmModalComponent
				open={showConfirmation}
				confirmText={t('COMMON.DELETE')}
				onDiscard={onDiscardConfirmation}
				title={t('COUNTRIES.DELETE_TITLE')}
				description={t('COUNTRIES.DELETE_DESCRIPTION')}
				note={t('COUNTRIES.DELETE_NOTE')}
				onConfirm={onDelete}
				isLoading={isDeleting}
			/>
		</>
	);
}

export default ListCountriesPage;
